import React from 'react';
import { connect } from 'react-redux';
import { Alert, Badge, Button, Form, Modal, Row, Col, Table } from 'react-bootstrap';
import { AsyncTypeahead, Typeahead } from 'react-bootstrap-typeahead';
import { ccsdCodeTypeaheadListFetch } from '../../../lib/redux/actions/ccsdCodesTypeahead';
import { ccsdCodeCostListFetch, ccsdCodeCostListUnload } from '../../../lib/redux/actions/ccsdCodeCosts';
import { datedCostBulkCreate } from '../../../lib/redux/actions/datedCosts';
import { consultantSpecialityListFetch } from '../../../lib/redux/actions/consultantSpeciality';
import { requests } from '../../../lib/helper/agent';
import * as API_ROUTES from '../../../lib/routing/api';
import MASpinner from '../../common/MASpinner';
import CcsdCodeCostAndTimelineIcon from '../../ccsdCodeCost/CcsdCodeCostAndTimelineIcon';
import { generalCurrencyFormatter } from '../../../lib/helper/formatting';

const stickyTh = { position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 };
const formatCost = (cost) => (cost != null ? generalCurrencyFormatter(cost) : '-');
const sortByConsultantName = (chis) =>
  [...chis].sort((a, b) => (a.consultant?.name || '').localeCompare(b.consultant?.name || ''));

class BulkFeeUpdateTab extends React.Component {
  constructor(props) {
    super(props);
    const today = new Date();
    this.state = {
      step: 'select',
      selectedCcsdCode: null,
      newCostC: '',
      newCostA: '',
      validFrom: today.toISOString().substring(0, 10),
      selectedIds: new Set(),
      isSubmitting: false,
      result: null,
      error: null,
      defaultCosts: null,
      isFetchingDefaults: false,
      addConsultantOptions: [],
      isSearchingConsultants: false,
      addedConsultants: [],
      updateDefaultCosts: false,
      specialityModalSpeciality: null,
      specialityModalMatches: [],
      specialityModalSelectedIds: new Set(),
      isSearchingSpeciality: false,
      specialityModalError: null,
    };
    this._addConsultantRef = React.createRef();
    this._specialityRef = React.createRef();
  }

  componentDidMount() {
    if (!this.props.consultantSpecialities || this.props.consultantSpecialities.length === 0) {
      this.props.consultantSpecialityListFetch();
    }
  }

  componentWillUnmount() {
    this.props.ccsdCodeCostListUnload();
  }

  handleCodeSelected(selected) {
    if (!selected || selected.length === 0) {
      this.setState({ selectedCcsdCode: null, defaultCosts: null, selectedIds: new Set() });
      this.props.ccsdCodeCostListUnload();
      return;
    }

    const code = selected[0];
    this.setState({ selectedCcsdCode: code, defaultCosts: null, selectedIds: new Set(), addedConsultants: [], updateDefaultCosts: false, isFetchingDefaults: true });

    // Fetch default (fee-assured) costs for this code + insurer
    requests
      .get(API_ROUTES.DEFAULT_CCSD_CODE_COSTS_LIST, {
        healthInsurer: this.props.healthInsurer.id,
        'ccsdCode.reference': code.reference,
        pagination: false,
      })
      .then((response) => {
        if (this.state.selectedCcsdCode?.id !== code.id) return;
        const defaults = response['hydra:member'] || [];
        const match = defaults.find((d) => d.ccsdCode?.id === code.id);
        this.setState({ defaultCosts: match || null, isFetchingDefaults: false });
      })
      .catch(() => {
        if (this.state.selectedCcsdCode?.id === code.id) {
          this.setState({ isFetchingDefaults: false });
        }
      });

    // Fetch non-fee-assured CcsdCodeCosts for this code + insurer
    this.props.ccsdCodeCostListFetch(1, {
      ccsdCode: code.id,
      'consultantHealthInsurer.healthInsurer': this.props.healthInsurer.id,
      'consultantHealthInsurer.feeAssured': false,
      'order[consultant.name]': 'ASC',
      pagination: false,
    });
  }

  handleSearch(query) {
    this.props.ccsdCodeTypeaheadListFetch(1, { reference: query });
  }

  getExcludedConsultantIds() {
    const existingConsultantIds = (this.props.ccsdCodeCosts || []).map(
      (ccc) => ccc.consultant?.id || ccc.consultantHealthInsurer?.consultant?.id
    );
    const addedConsultantIds = this.state.addedConsultants.map((chi) => chi.consultant?.id);
    return new Set([...existingConsultantIds, ...addedConsultantIds]);
  }

  handleConsultantSearch(query) {
    this.setState({ isSearchingConsultants: true });
    requests
      .get(API_ROUTES.CONSULTANT_HEALTH_INSURERS, {
        healthInsurer: this.props.healthInsurer.id,
        feeAssured: false,
        'consultant.isActive': true,
        'consultant.name': query,
        'order[consultant.name]': 'ASC',
        itemsPerPage: 20,
      })
      .then((response) => {
        const excludeIds = this.getExcludedConsultantIds();
        const options = (response['hydra:member'] || []).filter(
          (chi) => !excludeIds.has(chi.consultant?.id)
        );
        this.setState({ addConsultantOptions: options, isSearchingConsultants: false });
      })
      .catch(() => this.setState({ isSearchingConsultants: false }));
  }

  handleAddConsultant(selected) {
    if (!selected || selected.length === 0) return;
    const chi = selected[0];

    this.setState((prevState) => ({
      addedConsultants: sortByConsultantName([...prevState.addedConsultants, chi]),
      addConsultantOptions: [],
    }));

    if (this._addConsultantRef.current) {
      this._addConsultantRef.current.clear();
    }
  }

  handleRemoveAddedConsultant(chiId) {
    this.setState((prevState) => ({
      addedConsultants: prevState.addedConsultants.filter((chi) => chi.id !== chiId),
      selectedIds: (() => {
        const newIds = new Set(prevState.selectedIds);
        newIds.delete(`new-${chiId}`);
        return newIds;
      })(),
    }));
  }

  _specialityModalResetState() {
    return {
      specialityModalSpeciality: null,
      specialityModalMatches: [],
      specialityModalSelectedIds: new Set(),
      specialityModalError: null,
    };
  }

  handleSpecialitySelected(selected) {
    if (!selected || selected.length === 0) return;
    const speciality = selected[0];
    const excludeIds = this.getExcludedConsultantIds();

    this.setState({
      isSearchingSpeciality: true,
      specialityModalSpeciality: speciality,
      specialityModalMatches: [],
      specialityModalSelectedIds: new Set(),
      specialityModalError: null,
    });

    requests
      .get(API_ROUTES.CONSULTANT_HEALTH_INSURERS, {
        healthInsurer: this.props.healthInsurer.id,
        feeAssured: false,
        'consultant.isActive': true,
        'consultant.consultantSpecialities': speciality['@id'] || speciality.id,
        'order[consultant.name]': 'ASC',
        pagination: false,
      })
      .then((response) => {
        const matches = (response['hydra:member'] || []).filter(
          (chi) => !excludeIds.has(chi.consultant?.id)
        );
        this.setState({
          specialityModalMatches: matches,
          specialityModalSelectedIds: new Set(matches.map((chi) => chi.id)),
          isSearchingSpeciality: false,
        });
      })
      .catch(() => {
        this.setState({
          isSearchingSpeciality: false,
          specialityModalError: 'Failed to load consultants for the selected speciality. Please try again.',
        });
      });

    if (this._specialityRef.current) {
      this._specialityRef.current.clear();
    }
  }

  handleSpecialityModalClose() {
    this.setState(this._specialityModalResetState());
  }

  toggleSpecialityModalSelect(id) {
    const ids = new Set(this.state.specialityModalSelectedIds);
    if (ids.has(id)) {
      ids.delete(id);
    } else {
      ids.add(id);
    }
    this.setState({ specialityModalSelectedIds: ids });
  }

  toggleSpecialityModalSelectAll() {
    const { specialityModalMatches, specialityModalSelectedIds } = this.state;
    if (specialityModalSelectedIds.size === specialityModalMatches.length) {
      this.setState({ specialityModalSelectedIds: new Set() });
    } else {
      this.setState({ specialityModalSelectedIds: new Set(specialityModalMatches.map((chi) => chi.id)) });
    }
  }

  handleSpecialityModalConfirm() {
    const { specialityModalMatches, specialityModalSelectedIds } = this.state;
    const toAdd = specialityModalMatches.filter((chi) => specialityModalSelectedIds.has(chi.id));

    this.setState((prevState) => {
      const existingAddedIds = new Set(prevState.addedConsultants.map((chi) => chi.id));
      const merged = sortByConsultantName([
        ...prevState.addedConsultants,
        ...toAdd.filter((chi) => !existingAddedIds.has(chi.id)),
      ]);
      return {
        addedConsultants: merged,
        ...this._specialityModalResetState(),
      };
    });
  }

  getAllItems() {
    const { ccsdCodeCosts = [] } = this.props;
    const { addedConsultants } = this.state;
    return [
      ...ccsdCodeCosts.map((ccc) => ({ ...ccc, _type: 'existing' })),
      ...addedConsultants.map((chi) => ({ ...chi, _type: 'new', _id: `new-${chi.id}` })),
    ];
  }

  toggleSelectAll() {
    const allItems = this.getAllItems();
    const { selectedIds } = this.state;
    if (selectedIds.size === allItems.length) {
      this.setState({ selectedIds: new Set(), updateDefaultCosts: false });
    } else {
      this.setState({ selectedIds: new Set(allItems.map((c) => c._type === 'new' ? c._id : c.id)), updateDefaultCosts: false });
    }
  }

  toggleSelect(id) {
    const selectedIds = new Set(this.state.selectedIds);
    if (selectedIds.has(id)) {
      selectedIds.delete(id);
    } else {
      selectedIds.add(id);
    }
    this.setState({ selectedIds, updateDefaultCosts: false });
  }

  getCurrentCostForType(ccsdCodeCost, typeCode) {
    const currentCosts = ccsdCodeCost.currentCosts || [];
    const match = currentCosts.find((dc) => dc.consultantType?.code === typeCode);
    return match ? match.cost : null;
  }

  consultantHasType(item, typeCode) {
    const types = item.consultant?.consultantTypes || item.consultantHealthInsurer?.consultant?.consultantTypes || [];
    return types.some((ct) => ct.code === typeCode);
  }

  getDefaultCostForType(typeCode) {
    const { defaultCosts } = this.state;
    if (!defaultCosts) return null;
    const currentCosts = defaultCosts.currentCosts || [];
    const match = currentCosts.find((dc) => dc.consultantType?.code === typeCode);
    return match ? match.cost : null;
  }

  canPreview() {
    const { selectedIds, newCostC, newCostA, validFrom, selectedCcsdCode, updateDefaultCosts } = this.state;
    const hasTarget = selectedIds.size > 0 || updateDefaultCosts;
    return selectedCcsdCode && hasTarget && (newCostC !== '' || newCostA !== '') && validFrom;
  }

  handleUpdateDefaultsToggle(checked) {
    if (checked) {
      // Clear consultant selections when switching to defaults mode
      this.setState({ updateDefaultCosts: true, selectedIds: new Set() });
    } else {
      this.setState({ updateDefaultCosts: false });
    }
  }

  handlePreview() {
    this.setState({ step: 'preview' });
  }

  handleBack() {
    this.setState({ step: 'select' });
  }

  handleApply() {
    const { selectedIds, newCostC, newCostA, validFrom, selectedCcsdCode, addedConsultants, updateDefaultCosts, defaultCosts } = this.state;
    const { ccsdCodeCosts = [] } = this.props;

    const ccsdCodeCostIds = ccsdCodeCosts.filter((c) => selectedIds.has(c.id)).map((c) => c.id);
    const newConsultantHealthInsurerIds = addedConsultants
      .filter((chi) => selectedIds.has(`new-${chi.id}`))
      .map((chi) => chi.id);

    const entries = [];
    if (newCostC !== '' && !isNaN(parseFloat(newCostC))) {
      entries.push({ consultantTypeCode: 'C', cost: parseFloat(newCostC) });
    }
    if (newCostA !== '' && !isNaN(parseFloat(newCostA))) {
      entries.push({ consultantTypeCode: 'A', cost: parseFloat(newCostA) });
    }

    if (entries.length === 0) return;

    this.setState({ isSubmitting: true, error: null });

    this.props
      .datedCostBulkCreate({
        ccsdCodeCostIds,
        newConsultantHealthInsurerIds,
        ccsdCodeId: selectedCcsdCode.id,
        defaultCcsdCodeCostId: updateDefaultCosts && defaultCosts ? defaultCosts.id : null,
        entries,
        validFrom,
        healthInsurerId: this.props.healthInsurer.id,
      })
      .then((response) => {
        this.setState({ step: 'result', result: response, isSubmitting: false, addedConsultants: [] });

        // Refresh the consultant list to show updated costs
        this.props.ccsdCodeCostListFetch(1, {
          ccsdCode: selectedCcsdCode.id,
          'consultantHealthInsurer.healthInsurer': this.props.healthInsurer.id,
          'consultantHealthInsurer.feeAssured': false,
          'order[consultant.name]': 'ASC',
          pagination: false,
        });
      })
      .catch((error) => {
        this.setState({
          isSubmitting: false,
          error: error?.response?.body?.error || 'An error occurred while applying the bulk update. Please try again.',
        });
      });
  }

  handleReset() {
    this.setState({
      step: 'select',
      selectedCcsdCode: null,
      newCostC: '',
      newCostA: '',
      validFrom: new Date().toISOString().substring(0, 10),
      selectedIds: new Set(),
      result: null,
      error: null,
      defaultCosts: null,
      addedConsultants: [],
      updateDefaultCosts: false,
    });
    this.props.ccsdCodeCostListUnload();
  }



  renderSelectStep() {
    const { ccsdCodeCosts = [], isFetchingCcsdCodeCosts, ccsdCodes = [], isFetchingCcsdCodes, consultantTypes = [] } = this.props;
    const { selectedCcsdCode, newCostC, newCostA, validFrom, selectedIds, isFetchingDefaults } = this.state;
    const consultantTypeC = consultantTypes.find((ct) => ct.code === 'C');
    const consultantTypeA = consultantTypes.find((ct) => ct.code === 'A');

    const defaultC = this.getDefaultCostForType('C');
    const defaultA = this.getDefaultCostForType('A');
    const allItems = this.getAllItems();
    const totalCount = allItems.length;

    return (
      <>
        <Row className="mb-3">
          <Col sm={12} md={6}>
            <Form.Group className="mb-2">
              <Form.Label><strong>Select CCSD Code</strong></Form.Label>
              <AsyncTypeahead
                id="bulk-fee-ccsd-code"
                isLoading={isFetchingCcsdCodes}
                onSearch={this.handleSearch.bind(this)}
                onChange={this.handleCodeSelected.bind(this)}
                options={ccsdCodes || []}
                labelKey={(option) => `${option.reference}${option.name ? ' - ' + option.name : ''}`}
                placeholder="Search by code reference..."
                clearButton
                minLength={2}
              />
            </Form.Group>
          </Col>
          <Col sm={12} md={6}>
            {selectedCcsdCode && (
              <div className="p-2 bg-light rounded">
                <strong>Selected:</strong> {selectedCcsdCode.reference} {selectedCcsdCode.name ? `- ${selectedCcsdCode.name}` : ''}
                <br />
                <strong>Type:</strong> {selectedCcsdCode.ccsdCodeType?.name || '-'}
                {isFetchingDefaults ? (
                  <div className="mt-1"><small>Loading default costs...</small></div>
                ) : (
                  <div className="mt-1">
                    <strong>Fee Assured Defaults:</strong>{' '}
                    C = {formatCost(defaultC)}, A = {formatCost(defaultA)}
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>

        {selectedCcsdCode && (
          <>
            <Row className="mb-3">
              <Col sm={12} md={3}>
                <Form.Group>
                  <Form.Label>New Consultant (C) Cost</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={defaultC !== null ? `Default: ${defaultC}` : 'Leave blank to skip'}
                    value={newCostC}
                    onChange={(e) => this.setState({ newCostC: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={3}>
                <Form.Group>
                  <Form.Label>New Anaesthetist (A) Cost</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={defaultA !== null ? `Default: ${defaultA}` : 'Leave blank to skip'}
                    value={newCostA}
                    onChange={(e) => this.setState({ newCostA: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={3}>
                <Form.Group>
                  <Form.Label>Effective Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={validFrom}
                    onChange={(e) => this.setState({ validFrom: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            {this.state.defaultCosts && (
              <Form.Check
                type="checkbox"
                id="update-default-costs"
                label="Update fee assured default costs only (instead of individual consultants)"
                checked={this.state.updateDefaultCosts}
                onChange={(e) => this.handleUpdateDefaultsToggle(e.target.checked)}
                className="mb-3"
              />
            )}

            <>
                <h5 className="mb-2">
                  Non Fee-Assured Consultants
                  {!isFetchingCcsdCodeCosts && ` (${totalCount})`}
                </h5>

                {isFetchingCcsdCodeCosts ? (
                  <MASpinner msg="Loading consultants..." />
                ) : (
                  <>
                    {totalCount > 0 && (
                      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th style={stickyTh}>
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedIds.size === totalCount && totalCount > 0}
                                  onChange={this.toggleSelectAll.bind(this)}
                                  disabled={this.state.updateDefaultCosts}
                                />
                              </th>
                              <th style={stickyTh}>Consultant</th>
                              <th style={stickyTh}>Current Consultant Cost</th>
                              <th style={stickyTh}>Current Anaesthetist Cost</th>
                              <th style={stickyTh}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {ccsdCodeCosts.map((ccc) => (
                              <tr key={ccc.id}>
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    checked={selectedIds.has(ccc.id)}
                                    onChange={() => this.toggleSelect(ccc.id)}
                                    disabled={this.state.updateDefaultCosts}
                                  />
                                </td>
                                <td>
                                  {ccc.consultant?.name || ccc.consultantHealthInsurer?.consultant?.name || 'Unknown'}
                                  {' '}
                                  {(ccc.consultant?.consultantTypes || ccc.consultantHealthInsurer?.consultant?.consultantTypes || []).map((ct) => (
                                    <Badge key={ct.code} variant={ct.code === 'C' ? 'info' : 'warning'} className="ml-1">{ct.code}</Badge>
                                  ))}
                                </td>
                                <td className={!this.consultantHasType(ccc, 'C') ? 'text-muted' : ''}>
                                  {this.consultantHasType(ccc, 'C') && consultantTypeC ? (
                                    <CcsdCodeCostAndTimelineIcon ccsdCodeCostToUse={ccc} consultantType={consultantTypeC} />
                                  ) : <span className="text-muted">N/A</span>}
                                </td>
                                <td className={!this.consultantHasType(ccc, 'A') ? 'text-muted' : ''}>
                                  {this.consultantHasType(ccc, 'A') && consultantTypeA ? (
                                    <CcsdCodeCostAndTimelineIcon ccsdCodeCostToUse={ccc} consultantType={consultantTypeA} />
                                  ) : <span className="text-muted">N/A</span>}
                                </td>
                                <td></td>
                              </tr>
                            ))}
                            {this.state.addedConsultants.map((chi) => (
                              <tr key={`new-${chi.id}`} className="table-success">
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    checked={selectedIds.has(`new-${chi.id}`)}
                                    onChange={() => this.toggleSelect(`new-${chi.id}`)}
                                    disabled={this.state.updateDefaultCosts}
                                  />
                                </td>
                                <td>
                                  {chi.consultant?.name || 'Unknown'}
                                  {' '}
                                  {(chi.consultant?.consultantTypes || []).map((ct) => (
                                    <Badge key={ct.code} variant={ct.code === 'C' ? 'info' : 'warning'} className="ml-1">{ct.code}</Badge>
                                  ))}
                                  {' '}<Badge variant="light">New</Badge>
                                </td>
                                <td className={!this.consultantHasType(chi, 'C') ? 'text-muted' : ''}>
                                  {this.consultantHasType(chi, 'C') ? '-' : <span className="text-muted">N/A</span>}
                                </td>
                                <td className={!this.consultantHasType(chi, 'A') ? 'text-muted' : ''}>
                                  {this.consultantHasType(chi, 'A') ? '-' : <span className="text-muted">N/A</span>}
                                </td>
                                <td>
                                  <Button variant="outline-danger" size="sm" onClick={() => this.handleRemoveAddedConsultant(chi.id)}>
                                    <i className="fas fa-times" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                    {totalCount === 0 && (
                      <Alert variant="info">No non-fee-assured consultants have costs for this code yet.</Alert>
                    )}
                <Row className="mt-2 mb-2">
                  <Col sm={12} md={6}>
                    <Form.Label><strong>Add Consultant</strong></Form.Label>
                    <AsyncTypeahead
                      id="add-consultant-typeahead"
                      ref={this._addConsultantRef}
                      isLoading={this.state.isSearchingConsultants}
                      onSearch={this.handleConsultantSearch.bind(this)}
                      onChange={this.handleAddConsultant.bind(this)}
                      options={this.state.addConsultantOptions}
                      labelKey={(option) => {
                        const types = (option.consultant?.consultantTypes || []).map((ct) => ct.code).join('/');
                        return `${option.consultant?.name || 'Unknown'}${types ? ` (${types})` : ''}`;
                      }}
                      placeholder="Search for a consultant to add..."
                      clearButton
                      minLength={2}
                      disabled={this.state.updateDefaultCosts}
                    />
                  </Col>
                  <Col sm={12} md={6}>
                    <Form.Label><strong>Add Consultants by Speciality</strong></Form.Label>
                    <Typeahead
                      id="add-speciality-typeahead"
                      ref={this._specialityRef}
                      onChange={this.handleSpecialitySelected.bind(this)}
                      options={this.props.consultantSpecialities || []}
                      labelKey="name"
                      placeholder="Select a speciality..."
                      clearButton
                      disabled={this.state.updateDefaultCosts}
                    />
                  </Col>
                </Row>
                {(totalCount > 0 || this.state.updateDefaultCosts) && (
                  <div className="mt-2">
                    <Button
                      variant="primary"
                      disabled={!this.canPreview()}
                      onClick={this.handlePreview.bind(this)}
                    >
                      Preview Changes {this.state.updateDefaultCosts ? '(defaults)' : `(${selectedIds.size} selected)`}
                    </Button>
                  </div>
                )}
                  </>
                )}
              </>
          </>
        )}
      </>
    );
  }

  renderPreviewStep() {
    const { ccsdCodeCosts = [] } = this.props;
    const { selectedCcsdCode, newCostC, newCostA, validFrom, selectedIds, isSubmitting, error, addedConsultants, updateDefaultCosts } = this.state;
    const selectedCosts = ccsdCodeCosts.filter((c) => selectedIds.has(c.id));
    const selectedNewConsultants = addedConsultants.filter((chi) => selectedIds.has(`new-${chi.id}`));
    const totalSelected = selectedCosts.length + selectedNewConsultants.length;

    return (
      <>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning">
          <strong>Confirm Bulk Update</strong>
          <br />
          Code: <strong>{selectedCcsdCode.reference}</strong> {selectedCcsdCode.name ? `- ${selectedCcsdCode.name}` : ''}
          <br />
          {totalSelected > 0 && (
            <>Consultants: <strong>{totalSelected}</strong>{selectedNewConsultants.length > 0 && <> ({selectedNewConsultants.length} new)</>}<br /></>
          )}
          {updateDefaultCosts && <><strong>Default CCSD code cost for this insurer will be updated</strong><br /></>}
          {newCostC !== '' && <>New C Cost: <strong>{formatCost(newCostC)}</strong><br /></>}
          {newCostA !== '' && <>New A Cost: <strong>{formatCost(newCostA)}</strong><br /></>}
          Effective: <strong>{validFrom}</strong>
        </Alert>

        {totalSelected > 0 && (
        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th style={stickyTh}>Consultant</th>
                {newCostC !== '' && <th style={stickyTh}>C: Old &rarr; New</th>}
                {newCostA !== '' && <th style={stickyTh}>A: Old &rarr; New</th>}
              </tr>
            </thead>
            <tbody>
              {selectedCosts.map((ccc) => (
                <tr key={ccc.id}>
                  <td>
                    {ccc.consultant?.name || ccc.consultantHealthInsurer?.consultant?.name || 'Unknown'}
                    {' '}
                    {(ccc.consultant?.consultantTypes || ccc.consultantHealthInsurer?.consultant?.consultantTypes || []).map((ct) => (
                      <Badge key={ct.code} variant={ct.code === 'C' ? 'info' : 'warning'} className="ml-1">{ct.code}</Badge>
                    ))}
                  </td>
                  {newCostC !== '' && (
                    <td>
                      {this.consultantHasType(ccc, 'C') ? (
                        <>{formatCost(this.getCurrentCostForType(ccc, 'C'))} &rarr; <strong>{formatCost(newCostC)}</strong></>
                      ) : <span className="text-muted">N/A</span>}
                    </td>
                  )}
                  {newCostA !== '' && (
                    <td>
                      {this.consultantHasType(ccc, 'A') ? (
                        <>{formatCost(this.getCurrentCostForType(ccc, 'A'))} &rarr; <strong>{formatCost(newCostA)}</strong></>
                      ) : <span className="text-muted">N/A</span>}
                    </td>
                  )}
                </tr>
              ))}
              {selectedNewConsultants.map((chi) => (
                <tr key={`new-${chi.id}`} className="table-success">
                  <td>
                    {chi.consultant?.name || 'Unknown'}
                    {' '}
                    {(chi.consultant?.consultantTypes || []).map((ct) => (
                      <Badge key={ct.code} variant={ct.code === 'C' ? 'info' : 'warning'} className="ml-1">{ct.code}</Badge>
                    ))}
                    {' '}<Badge variant="light">New</Badge>
                  </td>
                  {newCostC !== '' && (
                    <td>
                      {this.consultantHasType(chi, 'C') ? (
                        <>- &rarr; <strong>{formatCost(newCostC)}</strong></>
                      ) : <span className="text-muted">N/A</span>}
                    </td>
                  )}
                  {newCostA !== '' && (
                    <td>
                      {this.consultantHasType(chi, 'A') ? (
                        <>- &rarr; <strong>{formatCost(newCostA)}</strong></>
                      ) : <span className="text-muted">N/A</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        )}

        <div className="mt-3">
          <Button variant="secondary" className="mr-2" onClick={this.handleBack.bind(this)} disabled={isSubmitting}>
            Back
          </Button>
          <Button variant="success" onClick={this.handleApply.bind(this)} disabled={isSubmitting}>
            {isSubmitting ? 'Applying...' : updateDefaultCosts ? 'Confirm & Apply to Defaults' : `Confirm & Apply to ${totalSelected} Consultants`}
          </Button>
        </div>
      </>
    );
  }

  renderResultStep() {
    const { result } = this.state;
    return (
      <>
        <Alert variant="success">
          <strong>Bulk update complete.</strong>{' '}
          {result?.created > 0 && <>Created {result.created} cost entries. </>}
          {result?.updated > 0 && <>Updated {result.updated} existing entries. </>}
        </Alert>
        <Button variant="primary" onClick={this.handleReset.bind(this)}>
          Start New Update
        </Button>
      </>
    );
  }

  render() {
    const { step } = this.state;

    return (
      <div>
        {step === 'select' && this.renderSelectStep()}
        {step === 'preview' && this.renderPreviewStep()}
        {step === 'result' && this.renderResultStep()}
        {this.renderSpecialityModal()}
      </div>
    );
  }

  renderSpecialityModal() {
    const { specialityModalSpeciality } = this.state;
    if (!specialityModalSpeciality) return null;

    const {
      specialityModalMatches,
      specialityModalSelectedIds,
      isSearchingSpeciality,
      specialityModalError,
    } = this.state;
    const allChecked = specialityModalMatches.length > 0 && specialityModalSelectedIds.size === specialityModalMatches.length;
    const selectedCount = specialityModalSelectedIds.size;

    return (
      <Modal show onHide={this.handleSpecialityModalClose.bind(this)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Add Consultants — {specialityModalSpeciality.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {specialityModalError && <Alert variant="danger">{specialityModalError}</Alert>}
          {isSearchingSpeciality ? (
            <MASpinner msg="Finding matching consultants..." />
          ) : specialityModalMatches.length === 0 ? (
            <Alert variant="info">
              No eligible consultants found for this speciality (they may already be in the list or may not have a non fee-assured relation to this insurer).
            </Alert>
          ) : (
            <>
              <p>
                Found <strong>{specialityModalMatches.length}</strong> non fee-assured consultant(s) with this speciality. Select the ones to add:
              </p>
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={stickyTh}>
                        <Form.Check
                          type="checkbox"
                          checked={allChecked}
                          onChange={this.toggleSpecialityModalSelectAll.bind(this)}
                        />
                      </th>
                      <th style={stickyTh}>Consultant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specialityModalMatches.map((chi) => (
                      <tr key={chi.id}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={specialityModalSelectedIds.has(chi.id)}
                            onChange={() => this.toggleSpecialityModalSelect(chi.id)}
                          />
                        </td>
                        <td>
                          {chi.consultant?.name || 'Unknown'}
                          {' '}
                          {(chi.consultant?.consultantTypes || []).map((ct) => (
                            <Badge key={ct.code} variant={ct.code === 'C' ? 'info' : 'warning'} className="ml-1">{ct.code}</Badge>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.handleSpecialityModalClose.bind(this)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={this.handleSpecialityModalConfirm.bind(this)}
            disabled={isSearchingSpeciality || selectedCount === 0}
          >
            Add {selectedCount} Consultant{selectedCount === 1 ? '' : 's'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  ccsdCodeCosts: state.ccsdCodeCosts.ccsdCodeCosts,
  isFetchingCcsdCodeCosts: state.ccsdCodeCosts.isFetchingCcsdCodeCosts,
  consultantTypes: state.consultantTypes.consultantTypes,
  ccsdCodes: state.ccsdCodesTypeahead.ccsdCodes,
  isFetchingCcsdCodes: state.ccsdCodesTypeahead.isFetchingCcsdCodes,
  consultantSpecialities: state.consultantSpecialities.consultantSpecialities,
});

const mapDispatchToProps = {
  ccsdCodeTypeaheadListFetch,
  ccsdCodeCostListFetch,
  ccsdCodeCostListUnload,
  datedCostBulkCreate,
  consultantSpecialityListFetch,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkFeeUpdateTab);
